import { FilterChangeEvent, initValues } from './Filter';
import { Button, Form, Input, InputNumber, Select, Tooltip } from 'antd';
import { ClearOutlined, DownOutlined, QuestionCircleOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { SnFormField } from './SnFormField';
import { usePlatform } from '../../hooks/usePlatformList';
import { StoreFormField } from './StoreFormField';
import { StyleFilter } from './style';
import FormItem from 'antd/lib/form/FormItem';
import { ApplyDateFormField } from './ApplyDateFormField';
import { TradeDateFormField } from './TradeDateFormField';
import { useMemo, useState } from 'react';
import { tradeReturnListFilterFoldCache } from 'services/cache';
import dayjs, { Dayjs } from 'lib/dayjs';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useStatusLabel } from '../StatusHistoryModal';

interface ShopeeReturnFilterProps {
  filterType: 'return' | 'cancel';
  onChange: (e: FilterChangeEvent) => void;
}

const defaultFormValues = {
  ...initValues,
  dateRange: [dayjs(dayjs().subtract(30, 'days').format('YYYY-MM-DD')), dayjs()],
};

const shopeeReturnReasonKeyMap: Record<string, string> = {
  BROKEN_PRODUCTS: 'domain.returnRefund.reason.brokenProducts',
  CHANGE_MIND: 'domain.returnRefund.reason.changeOfMind',
  DAMAGED_OTHERS: 'domain.returnRefund.reason.damagedOthers',
  DIFFERENT_DESCRIPTION: 'domain.returnRefund.reason.differentDescription',
  EXPECTATION_FAILED: 'domain.returnRefund.reason.expectationFailed',
  EXPIRED_PRODUCT: 'domain.returnRefund.reason.expiredProduct',
  FUNCTIONAL_DMG: 'domain.returnRefund.reason.functionalDamage',
  ITEM_FAKE: 'domain.returnRefund.reason.itemFake',
  ITEM_MISSING: 'domain.returnRefund.reason.itemMissing',
  ITEM_NOT_FIT: 'domain.returnRefund.reason.itemNotFit',
  LONG_DELIVERY_TIME: 'domain.returnRefund.reason.longDeliveryTime',
  NOT_RECEIPT: 'domain.returnRefund.reason.notReceived',
  OUTER_DAMAGED_PACKAGE: 'domain.returnRefund.reason.outerDamagedPackage',
  PHYSICAL_DMG: 'domain.returnRefund.reason.physicalDamage',
  SLIGHT_SCRATCH_DENTS: 'domain.returnRefund.reason.slightScratchDents',
  SPILLED_CONTENTS: 'domain.returnRefund.reason.spilledContents',
  SUSPICIOUS_PARCEL: 'domain.returnRefund.reason.suspiciousParcel',
  USED: 'domain.returnRefund.reason.used',
  WRONG_ADDRESS: 'domain.returnRefund.reason.wrongAddress',
  WRONG_ITEM: 'domain.returnRefund.reason.wrongItem',
  WRONG_ORDER_INFO: 'domain.returnRefund.reason.wrongOrderInfo',
};

export const ShopeeReturnFilter = (props: ShopeeReturnFilterProps) => {
  const { filterType, onChange } = props;
  const { t } = useTranslate();
  const platform = usePlatform()!;
  const [form] = Form.useForm();
  const [fold, setFold] = useState(tradeReturnListFilterFoldCache.get() ?? false);

  const [disabled, setDisabled] = useState(false);

  const [logisticsType, setLogisticsType] = useState<
    'leChatTraceStatus' | 'reverseLeChatTraceStatus'
  >('leChatTraceStatus');
  const [logisticsStatus, setLogisticsStatus] = useState<string[]>([]);

  const handleClearSubmit = useMemoizedFn(() => {
    form.resetFields();
    form.submit();
    setDisabled(false);
    setLogisticsStatus([]);
  });
  const handleSearchSubmit = useMemoizedFn(() => {
    form.submit();
  });
  const handleFormFinish = useMemoizedFn((e: any) => {
    const { sn, hasNote } = e;
    onChange({
      ...e,
      hasNote,
      sn: !sn ? undefined : sn,
    });
  });
  const handleValuesChange = useMemoizedFn((_, values: any) => {
    if ((values.snType === 'orderId' || values.snType === 'returnId') && values.sn) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  });
  const handleFold = useMemoizedFn(() => {
    setFold((prev) => {
      tradeReturnListFilterFoldCache.set(!prev);
      return !prev;
    });
  });

  const statusLabel = useStatusLabel();

  const {
    returnReasonSelectOptions,
    operateSelectOptions,
    returnTypeSelectOptions,
    displayStatusSelectOptions,
    logisticsStatusSelectOptions,
  } = useMemo(() => {
    return {
      returnReasonSelectOptions:
        platform !== 'SHOPEE'
          ? [
              {
                value: 'DID_NOT_RECEIVE_PARCEL',
                label: t('domain.returnRefund.reason.didNotReceiveParcel'),
              },
              {
                value: 'ITEM_ARRIVED_TOO_LATE',
                label: t('domain.returnRefund.reason.itemArrivedTooLate'),
              },
              {
                value: 'RECEIVED_EMPTY_PARCEL',
                label: t('domain.returnRefund.reason.receivedEmptyParcel'),
              },
              {
                value: 'ITEM_DOESNT_MATCH_DESCRIPTION',
                label: t('domain.returnRefund.reason.itemDoesntMatchDescription'),
              },
              {
                value: 'ITEM_DOESNT_WORK_PROPERLY',
                label: t('domain.returnRefund.reason.itemDoesntWorkProperly'),
              },
              { value: 'MISSING_ITEM', label: t('domain.returnRefund.reason.missingItem') },
              { value: 'DAMAGED', label: t('domain.returnRefund.reason.damaged') },
              {
                value: 'RECEIVED_WRONG_ITEM',
                label: t('domain.returnRefund.reason.receivedWrongItem'),
              },
              { value: 'CHANGE_OF_MIND', label: t('domain.returnRefund.reason.changeOfMind') },
              { value: 'COUNTERFEIT_ITEM', label: t('domain.returnRefund.reason.counterfeitItem') },
              { value: 'OTHER', label: t('domain.returnRefund.reason.other') },
            ]
          : [
              'BROKEN_PRODUCTS',
              'CHANGE_MIND',
              'DAMAGED_OTHERS',
              'DIFFERENT_DESCRIPTION',
              'EXPECTATION_FAILED',
              'EXPIRED_PRODUCT',
              'FUNCTIONAL_DMG',
              'ITEM_FAKE',
              'ITEM_MISSING',
              'ITEM_NOT_FIT',
              'LONG_DELIVERY_TIME',
              'NOT_RECEIPT',
              'OUTER_DAMAGED_PACKAGE',
              'PHYSICAL_DMG',
              'SLIGHT_SCRATCH_DENTS',
              'SPILLED_CONTENTS',
              'SUSPICIOUS_PARCEL',
              'USED',
              'WRONG_ADDRESS',
              'WRONG_ITEM',
              'WRONG_ORDER_INFO',
            ].map((value) => ({
              value,
              label: t(shopeeReturnReasonKeyMap[value]),
            })),
      operateSelectOptions:
        platform === 'LAZADA'
          ? [
              {
                value: 'CONFIRM_RETURN_REFUND',
                label: t('domain.returnRefund.type.returnRefund'),
              },
              {
                value: 'CONFIRM',
                label: t('domain.returnRefund.type.refundOnly'),
              },
              {
                value: 'REFUSE',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.reject'),
              },
            ]
          : isTiKTokUpperCase(platform)
          ? [
              {
                value: 'FULL_REFUND',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.approveRefundOnly'),
              },
              {
                value: 'CONFIRM_RETURN_REFUND',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.approveReturnRefund'),
              },
              {
                value: 'CONFIRM_REPLACEMENT',
                label: t('同意补发'),
              },
              {
                value: 'REFUSE',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.reject'),
              },
              {
                value: 'OFFER',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.partialRefund'),
              },
              {
                value: 'RETURN_REVIEW_OK',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.confirmReceipt'),
              },
              {
                value: 'RETURN_REVIEW_FAIL',
                label: t('包裹异常拒绝退款'),
              },
            ]
          : [
              {
                value: 'FULL_REFUND',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.fullRefund'),
              },
              {
                value: 'OFFER',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.offerPartialRefund'),
              },
              {
                value: 'WAIT_RETURN',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.waitForReturn'),
              },
              {
                value: 'SUBMIT_EVIDENCE',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.uploadEvidence'),
              },
              {
                value: 'DISPUTE_TO_SHOPEE',
                label: t('scene.orderWorkspace.returnRefundTab.filter.action.disputeToShopee'),
              },
            ],
      returnTypeSelectOptions: [
        {
          value: 'ONLY_REFUND',
          label: t('scene.orderWorkspace.returnRefundTab.filter.returnType.refundOnly'),
        },
        {
          value: 'RETURN_REFUND',
          label: t('scene.orderWorkspace.returnRefundTab.filter.returnType.returnRefund'),
        },
        ...(isTiKTokUpperCase(platform)
          ? [
              {
                value: 'CHANGE',
                label: t('scene.orderWorkspace.returnRefundTab.filter.returnType.exchange'),
              },
              {
                value: 'REPLACEMENT',
                label: t('补发'),
              },
            ]
          : []),
      ],
      displayStatusSelectOptions:
        platform === 'LAZADA'
          ? Object.entries(statusLabel).map(([value, label]) => {
              return {
                value,
                label,
              };
            })
          : isTiKTokUpperCase(platform)
          ? [
              {
                value: 'REFUND_REQUEST_PENDING',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.refundRequestPending',
                ),
              },
              {
                value: 'RETURN_REQUEST_PENDING',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.returnRequestPending',
                ),
              },
              {
                value: 'EXCHANGE_REQUEST_PENDING',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.replacementRequestPending',
                ),
              },
              {
                value: 'REPLACEMENT_REQUEST_PENDING',
                label: t('申请补发'),
              },
              {
                value: 'AWAITING_BUYER_SHIP',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.awaitingBuyerShip',
                ),
              },
              {
                value: 'BUYER_SHIPPED_ITEM',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.buyerShippedItem',
                ),
              },
              {
                value: 'REJECT_RECEIVE_PACKAGE',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.rejectReceivePackage',
                ),
              },
              {
                value: 'RETURN_OR_REFUND_REQUEST_SUCCESS',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.returnOrRefundRequestSuccess',
                ),
              },
              {
                value: 'EXCHANGE_REQUEST_REFUND_SUCCESS',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.replacementRequestRefundSuccess',
                ),
              },
              {
                value: 'REPLACEMENT_REQUEST_REFUND_SUCCESS',
                label: t('补发已完成'),
              },
              {
                value: 'REQUEST_REJECT',
                label: t('scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.requestReject'),
              },
              {
                value: 'REQUEST_CANCEL',
                label: t('scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.requestCancel'),
              },
              {
                value: 'REQUEST_COMPLETE',
                label: t(
                  'scene.orderWorkspace.returnRefundTab.filter.tiktokStatus.requestComplete',
                ),
              },
              {
                value: 'AWAITING_BUYER_RESPONSE',
                label: t('tiktok.AWAITING_BUYER_RESPONSE'),
              },
            ]
          : [
              'WAITING_EVIDENCE',
              'RETURN_PROCESSING',
              'VALIDATION_IN_PROGRESS',
              'REFUND_APPROVED',
              'EVIDENCE_SUBMITTED',
              'WAITING_BUYER_RESPONSE',
              'REFUND_REQUESTED',
              'RETURN_IN_PROGRESS',
              'REFUND_COMPLETED',
              'DISPUTE_RESOLVED',
              'COMPENSATION_APPROVED',
              'DISPUTE_REJECT',
              'DISPUTE_WAIT_EVIDENCE',
            ].map((value) => {
              return {
                value,
                label: t(`状态展示枚举.${value}`),
              };
            }),
      logisticsStatusSelectOptions: [
        'LE_CHAT_WAITING_COLLECT',
        'LE_CHAT_WAITING_SEND',
        'LE_CHAT_WAITING_SIGN',
        'LE_CHAT_SIGNED',
        'LE_CHAT_SEND_FAIL',
        'LE_CHAT_PACKAGE_LOST',
      ].map((status) => ({
        value: status,
        label: t(`leChatLogisticsStatus.${status}`),
      })),
    };
  }, [t, platform, statusLabel]);

  const disableDate = useMemoizedFn((current: Dayjs) => {
    const bigThanToday = current.isAfter(dayjs(), 'days');
    const lessThan90 = current.isBefore(dayjs().subtract(90, 'days'), 'days');
    return bigThanToday || lessThan90;
  });

  return (
    <div className="bg-fill-0 flex flex-col p-4">
      <StyleFilter
        form={form}
        initialValues={defaultFormValues}
        onFinish={handleFormFinish}
        onValuesChange={handleValuesChange}
      >
        <div className="filter-list">
          <SnFormField
            platform={platform}
            filterType={filterType}
            validate={false}
            onValuesChange={(values) => handleValuesChange({}, values)}
          />
          <StoreFormField disabled={disabled} platform={platform} filterType={filterType} />
          <FormItem name="reason">
            <Select
              dropdownMatchSelectWidth={false}
              disabled={disabled}
              mode="multiple"
              maxTagCount="responsive"
              placeholder={t('scene.orderWorkspace.returnRefundTab.filter.returnReason')}
              className="min-w-[160px]"
              allowClear
              showArrow
              options={returnReasonSelectOptions}
            />
          </FormItem>
          <ApplyDateFormField
            disabled={disabled}
            filterType={filterType}
            form={form}
            disabledDate={disableDate}
          />
        </div>
        {!fold ? (
          <>
            <div className="filter-list">
              <FormItem name="operate">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.availableAction')}
                  className="min-w-[10vw]"
                  allowClear
                  showArrow
                  options={operateSelectOptions}
                />
              </FormItem>
              <FormItem name="returnType">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.returnType')}
                  className="!w-[10vw]"
                  allowClear
                  showArrow
                  options={returnTypeSelectOptions}
                />
              </FormItem>
              <FormItem name="displayStatus">
                <Select
                  dropdownMatchSelectWidth={false}
                  disabled={disabled}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.returnStatus')}
                  className="min-w-[10vw]"
                  allowClear
                  showArrow
                  options={displayStatusSelectOptions}
                />
              </FormItem>
              <FormItem noStyle name="leChatTraceStatus" />
              <FormItem noStyle name="reverseLeChatTraceStatus" />
              {platform === 'SHOPEE' && (
                <Input.Group compact className="inline-flex !w-[auto] flex-nowrap">
                  <Select
                    style={{ maxWidth: '10vw' }}
                    disabled={disabled}
                    dropdownMatchSelectWidth={false}
                    value={logisticsType}
                    onChange={(logisticsType) => {
                      setLogisticsType(logisticsType);
                      form.setFieldsValue({
                        ...form.getFieldsValue(),
                        leChatTraceStatus: [],
                        reverseLeChatTraceStatus: [],
                        [logisticsType]: logisticsStatus,
                      });
                    }}
                    options={[
                      {
                        value: 'leChatTraceStatus',
                        label: t('scene.orderWorkspace.returnRefundTab.filter.forwardLogistics'),
                      },
                      {
                        value: 'reverseLeChatTraceStatus',
                        label: t('scene.orderWorkspace.returnRefundTab.filter.reverseLogistics'),
                      },
                    ]}
                  />
                  <Select
                    className="min-w-[10vw]"
                    disabled={disabled}
                    dropdownMatchSelectWidth={false}
                    mode="multiple"
                    placeholder={t('scene.orderWorkspace.returnRefundTab.filter.pleaseSelect')}
                    value={logisticsStatus}
                    onChange={(logisticsStatus) => {
                      setLogisticsStatus(logisticsStatus);
                      form.setFieldsValue({
                        ...form.getFieldsValue(),
                        [logisticsType]: logisticsStatus,
                      });
                    }}
                    options={logisticsStatusSelectOptions}
                  />
                </Input.Group>
              )}
              <div className="inline-flex flex-row items-center">
                <span className="text-nowrap mr-2">
                  {t('scene.orderWorkspace.returnRefundTab.filter.applicationAmount')}
                  <Tooltip
                    title={t('scene.orderWorkspace.returnRefundTab.filter.fixedCurrencyHint')}
                  >
                    <QuestionCircleOutlined className="mx-0.5" />
                  </Tooltip>
                  :
                </span>
                <FormItem name="refundAmountStart">
                  <InputNumber
                    disabled={disabled}
                    precision={2}
                    className="!w-[114px]"
                    placeholder={t('scene.orderWorkspace.returnRefundTab.filter.minAmount')}
                  />
                </FormItem>
                <span className="px-2">-</span>
                <FormItem name="refundAmountEnd">
                  <InputNumber
                    disabled={disabled}
                    precision={2}
                    className="!w-[114px]"
                    placeholder={t('scene.orderWorkspace.returnRefundTab.filter.maxAmount')}
                  />
                </FormItem>
              </div>
              {platform !== 'SHOPEE' && (
                <TradeDateFormField disabled={disabled} form={form} disabledDate={disableDate} />
              )}
            </div>
            <div className="filter-list">
              {platform === 'SHOPEE' && (
                <TradeDateFormField disabled={disabled} form={form} disabledDate={disableDate} />
              )}
              <FormItem name="hasNote" valuePropName="checked" className="min-w-[160px]">
                <Select
                  disabled={disabled}
                  placeholder={t('scene.orderWorkspace.returnRefundTab.filter.orderNote')}
                  allowClear
                >
                  <Select.Option value={1}>
                    {t('scene.orderWorkspace.returnRefundTab.filter.hasNote')}
                  </Select.Option>
                  <Select.Option value={2}>
                    {t('scene.orderWorkspace.returnRefundTab.filter.noNote')}
                  </Select.Option>
                </Select>
              </FormItem>
            </div>
          </>
        ) : null}
        <div className="flex gap-4">
          <FormItem noStyle>
            <Button
              type="primary"
              htmlType="submit"
              className="!min-w-32"
              onClick={handleSearchSubmit}
            >
              {t('scene.orderWorkspace.returnRefundTab.filter.searchOrder')}
            </Button>
          </FormItem>
          <Button type="link" className="!px-1" onClick={handleClearSubmit}>
            <ClearOutlined />
            {t('scene.orderWorkspace.returnRefundTab.filter.clearFilters')}
          </Button>
          <Button type="link" className="!px-1" onClick={handleFold}>
            {fold ? (
              <>
                {t('scene.orderWorkspace.returnRefundTab.filter.expandFilters')}
                <DownOutlined className="ml-2 text-xs" />
              </>
            ) : (
              <>
                {t('scene.orderWorkspace.returnRefundTab.filter.collapseFilters')}
                <UpOutlined className="ml-2 text-xs" />
              </>
            )}
          </Button>
        </div>
      </StyleFilter>
    </div>
  );
};
